import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
    Mail, Server, CheckCircle, XCircle, AlertCircle, RefreshCw,
    Send, Eye, EyeOff, Loader2, Shield, Cloud
} from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../utils/api';
import { logger } from '../utils/logger';

const EmailSettingsTab = () => {
    const [config, setConfig] = useState({
        provider: 'console',
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        smtp_from_email: '',
        smtp_use_tls: true,
        sendgrid_api_key: '',
        sendgrid_from_email: '',
        admin_notification_email: '',
        app_name: 'G6th Sense',
        require_otp_for_signup: true
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [saveMessage, setSaveMessage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [testEmail, setTestEmail] = useState('');

    // Fetch current email configuration
    useEffect(() => {
        fetchEmailConfig();
    }, []);

    const fetchEmailConfig = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/super-admin/settings/email');
            const data = response.data;

            if (data) {
                setConfig(prev => ({
                    ...prev,
                    provider: data.provider || 'console',
                    smtp_host: data.smtp_host || '',
                    smtp_port: data.smtp_port || 587,
                    smtp_user: data.smtp_user || '',
                    smtp_from_email: data.smtp_from_email || '',
                    smtp_use_tls: data.smtp_use_tls !== false,
                    sendgrid_from_email: data.sendgrid_from_email || '',
                    admin_notification_email: data.admin_notification_email || '',
                    app_name: data.app_name || 'G6th Sense',
                    require_otp_for_signup: data.require_otp_for_signup !== false,
                    // Don't overwrite passwords/keys - they come masked
                    smtp_password: data.smtp_password_configured ? '••••••••' : '',
                    sendgrid_api_key: data.sendgrid_api_key_configured ? '••••••••' : ''
                }));
            }
        } catch (error) {
            logger.error('Error fetching email config:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveMessage(null);
        setTestResult(null);

        try {
            // Don't send masked passwords
            const payload = { ...config };
            if (payload.smtp_password === '••••••••') {
                delete payload.smtp_password;
            }
            if (payload.sendgrid_api_key === '••••••••') {
                delete payload.sendgrid_api_key;
            }

            await api.put('/api/super-admin/settings/email', payload);
            setSaveMessage({ type: 'success', text: 'Email settings saved successfully!' });
            // Refresh to get updated masked values
            await fetchEmailConfig();
        } catch (error) {
            logger.error('Error saving email config:', error);
            const errorMessage = error.response?.data?.detail?.message
                || error.response?.data?.detail
                || 'Failed to save settings';
            setSaveMessage({ type: 'error', text: errorMessage });
        } finally {
            setSaving(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testEmail) {
            setTestResult({ success: false, message: 'Please enter a test email address' });
            return;
        }

        setTesting(true);
        setTestResult(null);

        try {
            const response = await api.post('/api/super-admin/settings/email/test', { test_email: testEmail });
            const data = response.data;
            setTestResult({
                success: data.success,
                message: data.message || (data.success ? 'Test email sent!' : 'Failed to send test email')
            });
        } catch (error) {
            logger.error('Error testing email:', error);
            const errorMessage = error.response?.data?.detail?.message
                || error.response?.data?.detail
                || 'Failed to send test email';
            setTestResult({ success: false, message: errorMessage });
        } finally {
            setTesting(false);
        }
    };

    const providers = [
        { id: 'console', name: 'Console (Development)', icon: Server, description: 'Logs emails to console - for development only' },
        { id: 'smtp', name: 'SMTP Server', icon: Mail, description: 'Use your own SMTP server (Gmail, Outlook, etc.)' },
        { id: 'sendgrid', name: 'SendGrid', icon: Cloud, description: 'SendGrid email delivery service' }
    ];

    if (loading) {
        return (
            <Card className="mt-6">
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[var(--g6-primary)]" />
                    <span className="ml-2">Loading email settings...</span>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="mt-6">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-[var(--g6-primary)]" />
                    <CardTitle>Email Configuration</CardTitle>
                </div>
                <CardDescription>
                    Configure email delivery for notifications, password resets, and alerts.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Provider Selection */}
                <div>
                    <label className="block text-sm font-medium mb-3">Email Provider</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {providers.map((provider) => (
                            <button
                                key={provider.id}
                                onClick={() => setConfig(prev => ({ ...prev, provider: provider.id }))}
                                className={cn(
                                    "p-4 rounded-lg border-2 text-left transition-all",
                                    config.provider === provider.id
                                        ? "border-[var(--g6-primary)] bg-[var(--g6-primary)]/5"
                                        : "border-gray-200 hover:border-gray-300"
                                )}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <provider.icon className={cn(
                                        "h-5 w-5",
                                        config.provider === provider.id ? "text-[var(--g6-primary)]" : "text-gray-500"
                                    )} />
                                    <span className="font-medium">{provider.name}</span>
                                </div>
                                <p className="text-xs text-gray-500">{provider.description}</p>
                            </button>
                        ))}
                    </div>
                </div>

                {/* SMTP Configuration */}
                {config.provider === 'smtp' && (
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium flex items-center gap-2">
                            <Server className="h-4 w-4" />
                            SMTP Server Settings
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">SMTP Host</label>
                                <Input
                                    value={config.smtp_host}
                                    onChange={(e) => setConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                                    placeholder="smtp.gmail.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">SMTP Port</label>
                                <Input
                                    type="number"
                                    value={config.smtp_port}
                                    onChange={(e) => setConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                                    placeholder="587"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Username / Email</label>
                                <Input
                                    value={config.smtp_user}
                                    onChange={(e) => setConfig(prev => ({ ...prev, smtp_user: e.target.value }))}
                                    placeholder="your-email@gmail.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Password / App Password</label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        value={config.smtp_password}
                                        onChange={(e) => setConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">From Email</label>
                                <Input
                                    value={config.smtp_from_email}
                                    onChange={(e) => setConfig(prev => ({ ...prev, smtp_from_email: e.target.value }))}
                                    placeholder="noreply@yourdomain.com"
                                />
                            </div>
                            <div className="flex items-center gap-2 pt-6">
                                <input
                                    type="checkbox"
                                    id="smtp_use_tls"
                                    checked={config.smtp_use_tls}
                                    onChange={(e) => setConfig(prev => ({ ...prev, smtp_use_tls: e.target.checked }))}
                                    className="h-4 w-4 text-[var(--g6-primary)] rounded"
                                />
                                <label htmlFor="smtp_use_tls" className="text-sm font-medium">Use TLS</label>
                            </div>
                        </div>
                    </div>
                )}

                {/* SendGrid Configuration */}
                {config.provider === 'sendgrid' && (
                    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium flex items-center gap-2">
                            <Cloud className="h-4 w-4" />
                            SendGrid Settings
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">SendGrid API Key</label>
                                <div className="relative">
                                    <Input
                                        type={showApiKey ? "text" : "password"}
                                        value={config.sendgrid_api_key}
                                        onChange={(e) => setConfig(prev => ({ ...prev, sendgrid_api_key: e.target.value }))}
                                        placeholder="SG.xxxxxx..."
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowApiKey(!showApiKey)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">From Email</label>
                                <Input
                                    value={config.sendgrid_from_email}
                                    onChange={(e) => setConfig(prev => ({ ...prev, sendgrid_from_email: e.target.value }))}
                                    placeholder="noreply@yourdomain.com"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Console Mode Notice */}
                {config.provider === 'console' && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                            <div>
                                <p className="font-medium text-yellow-800">Development Mode</p>
                                <p className="text-sm text-yellow-700">
                                    Emails will be logged to the console instead of being sent.
                                    Configure SMTP or SendGrid for production use.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* General Settings */}
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        General Settings
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Admin Notification Email</label>
                            <Input
                                value={config.admin_notification_email}
                                onChange={(e) => setConfig(prev => ({ ...prev, admin_notification_email: e.target.value }))}
                                placeholder="admin@yourdomain.com"
                            />
                            <p className="text-xs text-gray-500 mt-1">Receives system alerts and notifications</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">App Name</label>
                            <Input
                                value={config.app_name}
                                onChange={(e) => setConfig(prev => ({ ...prev, app_name: e.target.value }))}
                                placeholder="G6th Sense"
                            />
                            <p className="text-xs text-gray-500 mt-1">Used in email templates</p>
                        </div>
                    </div>

                    {/* OTP Toggle */}
                    <div className="pt-4 border-t">
                        <div className="flex items-center justify-between">
                            <div>
                                <label className="text-sm font-medium">Require Email Verification for Signup</label>
                                <p className="text-xs text-gray-500">When enabled, new tenant admins must verify their email with a 6-digit code</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setConfig(prev => ({ ...prev, require_otp_for_signup: !prev.require_otp_for_signup }))}
                                className={cn(
                                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                    config.require_otp_for_signup ? "bg-[var(--g6-primary)]" : "bg-gray-300"
                                )}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                        config.require_otp_for_signup ? "translate-x-6" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Test Email */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        Test Email Configuration
                    </h4>
                    <div className="flex gap-3">
                        <Input
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="Enter email to send test"
                            className="flex-1 bg-white"
                        />
                        <Button
                            onClick={handleTestEmail}
                            disabled={testing || !testEmail}
                            variant="outline"
                        >
                            {testing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4 mr-2" />
                                    Send Test
                                </>
                            )}
                        </Button>
                    </div>
                    {testResult && (
                        <div className={cn(
                            "flex items-center gap-2 p-3 rounded-lg",
                            testResult.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        )}>
                            {testResult.success ? (
                                <CheckCircle className="h-4 w-4" />
                            ) : (
                                <XCircle className="h-4 w-4" />
                            )}
                            {testResult.message}
                        </div>
                    )}
                </div>

                {/* Save Message */}
                {saveMessage && (
                    <div className={cn(
                        "flex items-center gap-2 p-3 rounded-lg",
                        saveMessage.type === 'success' ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                    )}>
                        {saveMessage.type === 'success' ? (
                            <CheckCircle className="h-4 w-4" />
                        ) : (
                            <XCircle className="h-4 w-4" />
                        )}
                        {saveMessage.text}
                    </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-[var(--g6-primary)] hover:bg-[var(--g6-primary)]/90"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Save Email Settings
                            </>
                        )}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default EmailSettingsTab;
